import {
  mobileAccordions,
  resetSecondaryMenu,
  resetTertiaryMenu,
  navScroll,
  switchContent,
  switchTertiaryContent,
} from "../utilities/nav-utils"
import { mediaQueries } from "../utilities/utils"
import { closeAllToggletips } from "../utilities/toggletip-engine"

const ensureGlobalNavMenuDrawerAccordionHandler = () => {
  if (window.__navMenuDrawerAccordionHandlerBound) return
  window.__navMenuDrawerAccordionHandlerBound = true

  const prefersReducedMotion = () => {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  const animateAccordionPanel = (panel, open) => {
    if (!panel) return

    if (prefersReducedMotion()) {
      panel.style.height = ""
      panel.style.overflow = ""
      panel.hidden = !open
      return
    }

    const currentHeight = panel.getBoundingClientRect().height
    panel.style.overflow = "hidden"

    if (open) {
      panel.hidden = false
      const startHeight = currentHeight || 0
      const endHeight = panel.scrollHeight

      const animation = panel.animate(
        { height: [`${startHeight}px`, `${endHeight}px`] },
        { duration: 250, easing: "ease-out" },
      )
      animation.onfinish = () => {
        panel.style.height = ""
        panel.style.overflow = ""
      }
    } else {
      const startHeight = currentHeight || panel.scrollHeight
      const animation = panel.animate(
        { height: [`${startHeight}px`, "0px"] },
        { duration: 250, easing: "ease-out" },
      )
      animation.onfinish = () => {
        panel.hidden = true
        panel.style.height = ""
        panel.style.overflow = ""
      }
    }
  }

  const getTriggerAndPanel = (wrapper) => {
    const trigger = wrapper?.firstElementChild?.classList?.contains("nav__mobile-accordion-opener")
      ? wrapper.firstElementChild
      : null
    const panel = trigger?.nextElementSibling?.classList?.contains("nav__mobile-dropdown__content-wrapper")
      ? trigger.nextElementSibling
      : null
    return { trigger, panel }
  }

  const closeDescendants = (wrapper) => {
    wrapper.querySelectorAll(".nav__mobile-dropdown-wrapper").forEach((childWrapper) => {
      childWrapper.classList.remove("nav__mobile-dropdown-wrapper--open")
      const { trigger, panel } = getTriggerAndPanel(childWrapper)
      if (trigger) trigger.setAttribute("aria-expanded", "false")
      if (panel) animateAccordionPanel(panel, false)
    })
  }

  const setOpen = (wrapper, open) => {
    wrapper.classList.toggle("nav__mobile-dropdown-wrapper--open", open)
    const { trigger, panel } = getTriggerAndPanel(wrapper)
    if (trigger) trigger.setAttribute("aria-expanded", open ? "true" : "false")
    if (panel) animateAccordionPanel(panel, open)
    if (!open) closeDescendants(wrapper)
  }

  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null
    if (!target) return
    const trigger = target.closest(".nav__mobile-accordion-opener")
    if (!trigger || trigger.tagName !== "BUTTON") return

    const drawer = trigger.closest('drawer-component[data-drawer="nav-menu"]')
    if (!drawer) return

    const navEl = trigger.closest(".nav")
    if (!navEl || !drawer.contains(navEl)) return

    // Only handle when drawer is in accordion mode for the current viewport.
    const drawerMenuStyle = mediaQueries.isLargeDown.matches
      ? (navEl.dataset.drawerMenuStyle || "accordion")
      : (navEl.dataset.desktopDrawerMenuStyle || navEl.dataset.drawerMenuStyle || "accordion")
    if (drawerMenuStyle !== "accordion") return

    const wrapper = trigger.closest(".nav__mobile-dropdown-wrapper")
    if (!wrapper || !navEl.contains(wrapper)) return

    // Accordion triggers are buttons; prevent interference from other handlers.
    event.preventDefault()
    event.stopPropagation()

    const isOpen = wrapper.classList.contains("nav__mobile-dropdown-wrapper--open")

    // Close sibling accordions at the same level (matches legacy behavior)
    const wrapperParent = wrapper.parentElement
    const siblingContainer = wrapperParent?.tagName === "LI" ? wrapperParent.parentElement : wrapperParent
    const siblings = Array.from(siblingContainer?.children || []).flatMap((item) => {
      if (item === wrapper) return []
      if (item.classList?.contains("nav__mobile-dropdown-wrapper")) return [item]
      const nestedWrapper = item.querySelector?.(":scope > .nav__mobile-dropdown-wrapper")
      return nestedWrapper && nestedWrapper !== wrapper ? [nestedWrapper] : []
    })
    siblings.forEach((sib) => setOpen(sib, false))

    setOpen(wrapper, !isOpen)
  }, true)
}

const initializeNavMenuDrawerNavUX = ({ token } = {}) => {
  const drawerNavEls = Array.from(
    document.querySelectorAll('drawer-component[data-drawer="nav-menu"] .nav'),
  )
  if (!drawerNavEls.length) return

  drawerNavEls.forEach((el) => {
    el.props = el.props || {}

    const drawerMenuStyle = mediaQueries.isLargeDown.matches
      ? (el.dataset.drawerMenuStyle || "accordion")
      : (el.dataset.desktopDrawerMenuStyle || el.dataset.drawerMenuStyle || "accordion")
    const drawerAccordion = drawerMenuStyle === "accordion"
    const mode = drawerAccordion ? "accordion" : "paged"

    if (drawerAccordion) {
      // No per-node binding: global delegated handler survives all Theme Editor DOM swaps.
      ensureGlobalNavMenuDrawerAccordionHandler()
    }

    // Avoid double-binding within the same Theme Editor re-render cycle,
    // but still allow initialization if the mode changed (accordion <-> paged).
    if (token && el.dataset.navDrawerUxToken === token && el.dataset.navDrawerUxMode === mode) return
    if (token) {
      el.dataset.navDrawerUxToken = token
      el.dataset.navDrawerUxMode = mode
    }

    if (drawerAccordion) {
      return
    }

    // Theme Editor swaps drawer markup in-place. Clear binding flags so we can
    // reliably re-bind paged navigation after section setting changes.
    el.querySelectorAll(".nav__main-menu-link").forEach((btn) => {
      if (!btn?.dataset) return
      delete btn.dataset.navPagedBound
    })
    el.querySelectorAll(".nav__secondary-menu__close").forEach((btn) => {
      if (!btn?.dataset) return
      delete btn.dataset.navPagedCloseBound
    })
    el.querySelectorAll(".nav__tertiary-menu__close").forEach((btn) => {
      if (!btn?.dataset) return
      delete btn.dataset.navPagedTertiaryCloseBound
    })

    // Reset state so paged menus start from a consistent baseline.
    resetSecondaryMenu(el)
    resetTertiaryMenu(el)

    // Paged drawer: L2/L3 pages via switchContent / switchTertiaryContent
    const innerContainer = el.querySelector(".nav--mobile__inner-container")

    const scrollToTop = () => {
      if (!innerContainer) return
      requestAnimationFrame(() => {
        innerContainer.scrollTop = 0
        innerContainer.scrollLeft = 0
        if (typeof innerContainer.scrollTo === "function") {
          innerContainer.scrollTo({ top: 0, left: 0, behavior: "auto" })
        }
      })
    }

    el.querySelectorAll(".nav__main-menu-link").forEach((button) => {
      if (button.dataset.navPagedBound === "true") return
      button.dataset.navPagedBound = "true"

      button.addEventListener("click", () => {
        const targetId = button.getAttribute("data-targetmenu") || ""
        if (!targetId) return
        if (targetId.startsWith("navigation-item-l3-")) {
          switchTertiaryContent(button, el)
          scrollToTop()
        } else {
          el.classList.remove("nav--tertiary-menu-open")
          resetTertiaryMenu(el)
          el.classList.add("nav--secondary-menu-open")
          switchContent(button, el)
          scrollToTop()
        }
      })
    })

    el.querySelectorAll(".nav__secondary-menu__close").forEach((button) => {
      if (button.dataset.navPagedCloseBound === "true") return
      button.dataset.navPagedCloseBound = "true"

      button.addEventListener("click", () => {
        resetSecondaryMenu(el)
        resetTertiaryMenu(el)
        scrollToTop()
      })
    })

    el.querySelectorAll(".nav__tertiary-menu__close").forEach((button) => {
      if (button.dataset.navPagedTertiaryCloseBound === "true") return
      button.dataset.navPagedTertiaryCloseBound = "true"

      button.addEventListener("click", () => {
        resetTertiaryMenu(el)
        scrollToTop()
      })
    })
  })
}

if (!window.__navMenuDrawerNavUXListenerBound) {
  window.__navMenuDrawerNavUXListenerBound = true
  const reinit = () => {
    const token = String(Date.now()) + "-" + String(Math.random()).slice(2)
    initializeNavMenuDrawerNavUX({ token })
    // Safety retry: Theme Editor DOM can settle after our first pass.
    setTimeout(() => initializeNavMenuDrawerNavUX({ token }), 50)
  }
  document.addEventListener("drawer:reinitialize", reinit)

  // Theme Editor can re-render sections without guaranteeing timing relative to our custom elements.
  if (typeof Shopify !== "undefined" && Shopify.designMode) {
    document.addEventListener("shopify:section:load", reinit)
  }
}

class NavigationMain extends HTMLElement {
  static mirroredBodyHeaderClasses = [
    "header-visible",
    "header--sticky-engaged",
    "header--sticky-always",
    "header--hidden",
    "header--visible",
    "sticky-active",
    "reached-top",
    "nav-wrapper--solid",
  ]

  connectedCallback() {
    this.headerEl = this.closest(".shopify-section-group-main-header.main-navigation")
    this.navDrawerEl = null
    this.navigationItems = this.querySelectorAll("toggletip-engine")

    this.dropdownOpen = this.dataset.dropdownOpen || this.dataset.ux || "hover"
    this.ux = this.dropdownOpen
    this.uxPosition = this.dataset.uxPosition
    this.menuActiveStyle = this.dataset.menuActiveStyle || "none"
    this.solidOnScroll = this.dataset.solidOnScroll === "true"
    this.overlayFirstSection = this.dataset.overlayFirstSection === "true"
    this.overlayEnabledForTemplate = this.dataset.overlayEnabledForTemplate === "true"
    this.defaultScheme = this.dataset.defaultColorScheme
    this.overlayScheme = this.dataset.overlayColorScheme
    this.sectionWrapper = this.closest(".shopify-section")?.querySelector(".section__wrapper")
    this.firstMainSectionEl = document.querySelector("main#main-content > .shopify-section:first-child")
    this.megaMenuEls = Array.from(this.querySelectorAll(".mega-menu"))
    this.hasOverlayLogos = !!this.querySelector(".header__logo--overlay")

    this.headerEl.props = {}
    this.headerEl.props.lastScroll = window.scrollY

    this.resetHeader()
    this.#storeMegaMenuSchemes()
    this.#bindNavDropdownBackdrop()

    if (this.uxPosition === "fixed") {
      this.headerEl.classList.add("header--sticky-always")
    }

    if (this.uxPosition === "off" || this.uxPosition === "hide") {
      this.observeStickySubheader()
    }

    this.initOverlay()

    this.debouncedUpdateNavDrawerLayout = this._debounce(this.#updateNavDrawerLayout, 80)
    window.addEventListener("scroll", this.handleScroll, { passive: true })
    this.handleScroll()

    this.resetOnResize()
    this.enableNavUX()
    initializeNavMenuDrawerNavUX()
    this.#setupNavDrawerClose()
    this.#updateNavDrawerLayout()
    window.addEventListener("resize", this.debouncedUpdateNavDrawerLayout)
    window.addEventListener("orientationchange", this.debouncedUpdateNavDrawerLayout)
  }

  #setupNavDrawerClose() {
    // Nav drawer is portaled to document.body (data-append), so drawer:* events
    // never reach headerEl — listen on document instead.
    this._handleNavDrawerOpen = (e) => {
      if (e.target?.dataset?.drawer !== "nav-menu") return
      this.#updateNavDrawerLayout()
    }
    this._handleNavDrawerClose = (e) => {
      if (e.target?.dataset?.drawer !== "nav-menu") return
      const nav = e.target.querySelector(".nav")
      if (nav) {
        resetSecondaryMenu(nav)
        resetTertiaryMenu(nav)
      }
      this.#updateNavDrawerLayout()
    }
    document.addEventListener("drawer:open", this._handleNavDrawerOpen)
    document.addEventListener("drawer:close", this._handleNavDrawerClose)
  }

  #resolveNavDrawerEl() {
    if (this.navDrawerEl && this.navDrawerEl.isConnected) return this.navDrawerEl

    const scopedDrawer = this.headerEl?.querySelector('drawer-component[data-drawer="nav-menu"]')
    if (scopedDrawer) {
      this.navDrawerEl = scopedDrawer
      return this.navDrawerEl
    }

    const globalDrawer = document.querySelector('drawer-component[data-drawer="nav-menu"]')
    if (globalDrawer) {
      this.navDrawerEl = globalDrawer
      return this.navDrawerEl
    }

    return null
  }

  // resets the header to default by removing navScroll utility changes
  resetHeader = () => {
    this.headerEl.classList.remove(...NavigationMain.mirroredBodyHeaderClasses)
    this.#syncBodyHeaderClasses()
  }

  handleScroll = () => {
    if (this.uxPosition !== "off") {
      navScroll(this.headerEl)

      const atTop = this.headerEl.classList.contains("reached-top")
      const isStickyNow = this.headerEl.classList.contains("header--sticky-engaged") || this.headerEl.classList.contains("header--sticky-always")

      if (this.solidOnScroll) {
        isStickyNow && !atTop ? this.headerEl.classList.add("nav-wrapper--solid") : this.headerEl.classList.remove("nav-wrapper--solid")
      }

      this.#syncBodyHeaderClasses()
      this.#updateOverlayColorScheme({ atTop, isStickyNow })
    }

    this.#syncNavDrawerLayoutOnScroll()
  }

  #syncNavDrawerLayoutOnScroll() {
    if (!mediaQueries.isLargeDown.matches) return

    const navDrawer = this.#resolveNavDrawerEl()
    if (navDrawer?.classList.contains("open")) {
      this.#updateNavDrawerLayout()
    } else {
      this.debouncedUpdateNavDrawerLayout()
    }
  }

  #syncBodyHeaderClasses() {
    if (!document.body || !this.headerEl) return

    NavigationMain.mirroredBodyHeaderClasses.forEach((className) => {
      document.body.classList.toggle(className, this.headerEl.classList.contains(className))
    })
  }

  initOverlay() {
    if (!this.overlayFirstSection || !this.overlayEnabledForTemplate) return

    document.body.classList.add("nav-overlay-active")

    this.#updateOverlayColorScheme()
  }

  #bindNavDropdownBackdrop() {
    const backdrop = this.headerEl?.querySelector("[data-nav-dropdown-backdrop]")
    if (!backdrop || backdrop.dataset.bound === "true") return

    // Nav uses transform for sticky hide/show, which traps position:fixed inside the header box.
    if (backdrop.parentElement !== document.body) {
      document.body.appendChild(backdrop)
    }

    backdrop.dataset.bound = "true"
    backdrop.addEventListener("click", () => closeAllToggletips(true))
  }

  #storeMegaMenuSchemes() {
    // Mega menus can have their own scheme (from blocks). We preserve it so we can
    // temporarily swap to overlay/default schemes and then restore.
    this.megaMenuEls?.forEach((el) => {
      if (el.dataset.baseColorScheme) return
      const baseScheme = Array.from(el.classList).find((c) => c.startsWith("color-"))?.replace("color-", "")
      if (baseScheme) el.dataset.baseColorScheme = baseScheme
    })
  }

  #applySchemeToMegaMenus(scheme) {
    if (!scheme) return
    this.megaMenuEls?.forEach((el) => {
      const base = el.dataset.baseColorScheme
      if (base) el.classList.remove("color-" + base)
      if (this.defaultScheme) el.classList.remove("color-" + this.defaultScheme)
      if (this.overlayScheme) el.classList.remove("color-" + this.overlayScheme)
      el.classList.add("color-" + scheme)
    })
  }

  #restoreMegaMenuSchemes() {
    this.megaMenuEls?.forEach((el) => {
      const base = el.dataset.baseColorScheme
      if (!base) return
      if (this.defaultScheme) el.classList.remove("color-" + this.defaultScheme)
      if (this.overlayScheme) el.classList.remove("color-" + this.overlayScheme)
      el.classList.add("color-" + base)
    })
  }

  #isWithinFirstSectionBoundary() {
    // Overlay scheme should apply while the header is visually overlapping the first section in <main>.
    // We treat that as "first section bottom is below the header bottom".
    const firstSection = this.firstMainSectionEl
    if (!firstSection) return false

    const firstRect = firstSection.getBoundingClientRect()
    const headerRect = this.headerEl?.getBoundingClientRect()
    if (!headerRect) return false

    return firstRect.bottom > headerRect.bottom
  }

  #updateOverlayColorScheme({ atTop, isStickyNow } = {}) {
    if (!this.overlayFirstSection || !this.overlayEnabledForTemplate) return
    if (!this.sectionWrapper || !this.defaultScheme || !this.overlayScheme) return

    const isAtTop = atTop ?? this.headerEl.classList.contains("reached-top")
    const stickyEngaged = isStickyNow ?? (this.headerEl.classList.contains("header--sticky-engaged") || this.headerEl.classList.contains("header--sticky-always"))

    // Overlay scheme rules:
    // - Sticky configured (even if not yet engaged): apply overlay scheme while we're still within the first main section boundary.
    // - Sticky disabled: keep legacy behavior (overlay scheme only at very top).
    // Note: solid-on-scroll should never affect color scheme; it only forces opacity/blur solid.
    const withinFirstSection = this.#isWithinFirstSectionBoundary()
    const stickyConfigured = this.uxPosition !== "off"
    const shouldUseOverlayScheme = stickyConfigured ? withinFirstSection : (this.uxPosition === "off" ? true : (stickyEngaged ? false : isAtTop))

    const defaultClass = "color-" + this.defaultScheme
    const overlayClass = "color-" + this.overlayScheme
    const schemesMatch = this.defaultScheme === this.overlayScheme

    if (schemesMatch) {
      // Default and overlay share one class; toggling both would add then remove it.
      this.sectionWrapper.classList.add(defaultClass)
    } else {
      this.sectionWrapper.classList.toggle(defaultClass, !shouldUseOverlayScheme)
      this.sectionWrapper.classList.toggle(overlayClass, shouldUseOverlayScheme)
    }

    if (shouldUseOverlayScheme) {
      this.#applySchemeToMegaMenus(this.overlayScheme)
    } else {
      // When not overlaying, mega menus should go back to their block-defined schemes.
      this.#restoreMegaMenuSchemes()
    }

    if (this.hasOverlayLogos) {
      this.headerEl?.classList.toggle("header--overlay-scheme-active", shouldUseOverlayScheme)
    }
  }

  enableNavUX() {
    // --- Start of new and improved hover logic ---
    const applyHoverWithGracePeriod = (triggerElement) => {
      let closeTimer;
      const dropdown = triggerElement.querySelector("[data-tooltip]");

      // Find the actual focusable element (the link/button inside)
      const focusableElement = triggerElement.querySelector('[data-tooltip-trigger]') || triggerElement;

      const openMenu = () => {
        if(closeTimer) clearTimeout(closeTimer);
        // For parent toggletips, we need to handle closing others
        if (triggerElement.classList.contains('toggletip-parent')) {
          handleParentToggletip(triggerElement, 'open');
        } else {
          closeAllToggletips(true, triggerElement);
          triggerElement.open();
        }
      };

      const startCloseTimer = () => {
        closeTimer = setTimeout(() => {
          if (triggerElement.classList.contains('toggletip-parent')) {
            handleParentToggletip(triggerElement, 'close');
          } else {
            triggerElement.close();
          }
        }, 150); // 150ms delay
      };

      triggerElement.addEventListener("mouseenter", openMenu);
      triggerElement.addEventListener("mouseleave", startCloseTimer);

      focusableElement.addEventListener("focus", (e) => {
        openMenu();
      });

      // Use a more sophisticated blur handling for dropdowns
      focusableElement.addEventListener("blur", (e) => {
        // Use a small delay to allow focus to move to dropdown content
        setTimeout(() => {
          // Check if focus is still within the toggletip structure
          const activeElement = document.activeElement;
          const isWithinToggletip = triggerElement.contains(activeElement) ||
                                   (triggerElement.querySelector('[data-tooltip]') &&
                                    triggerElement.querySelector('[data-tooltip]').contains(activeElement));

          if (!isWithinToggletip) {
            startCloseTimer();
          }
        }, 50);
      });

      // Add keyboard activation support (Space opens menu, Enter navigates)
      focusableElement.addEventListener("keydown", (e) => {
        if (e.key === ' ') {
          e.preventDefault();
          openMenu();
        } else if (e.key === 'Enter') {
          // Let the default behavior happen (navigate to the link)
        }
      });

      if (dropdown) {
        dropdown.addEventListener("mouseenter", () => {
          if(closeTimer) clearTimeout(closeTimer);
        });
        dropdown.addEventListener("mouseleave", startCloseTimer);
      }
    };
    // --- End of new and improved hover logic ---

    const getNavSectionRoot = () => this.closest(".shopify-section") || this

    const getDropdownMenus = () =>
      Array.from(
        getNavSectionRoot().querySelectorAll(
          ".nav.nav--horizontal .nav__menu, .nav.nav--vertical .nav__menu",
        ),
      )

    const getDropdownSubmenus = () =>
      Array.from(
        getNavSectionRoot().querySelectorAll(
          ".nav.nav--horizontal .nav__submenu, .nav.nav--vertical .nav__submenu",
        ),
      )

    // Menu active styling (highlight/underline): top-level + horizontal/vertical dropdown L2
    const setActiveMenuItem = (toggletip, open) => {
      if (this.menuActiveStyle === "none") return

      const headerMenu = this.querySelector(".header__menu")

      if (!open && !toggletip) {
        headerMenu?.querySelectorAll(".main-nav-item").forEach((el) => {
          el.classList.remove("main-nav-item--open")
        })
        getDropdownMenus().forEach((menu) => {
          menu.querySelectorAll(":scope > li").forEach((li) => {
            li.classList.remove("nav__menu-item--open")
          })
        })
        return
      }

      const mainNavItem = toggletip?.closest(".main-nav-item")

      if (mainNavItem && headerMenu) {
        headerMenu.querySelectorAll(".main-nav-item").forEach((el) => {
          el.classList.remove("main-nav-item--open")
        })
        if (open) mainNavItem.classList.add("main-nav-item--open")
        return
      }

      const dropdownMenu = toggletip?.closest(
        ".nav.nav--horizontal .nav__menu, .nav.nav--vertical .nav__menu",
      )
      if (!dropdownMenu) return

      dropdownMenu.querySelectorAll(":scope > li").forEach((li) => {
        li.classList.remove("nav__menu-item--open")
      })
      if (open && toggletip) {
        const listItem = toggletip.closest("li")
        if (listItem && dropdownMenu.contains(listItem)) {
          listItem.classList.add("nav__menu-item--open")
        }
      }
    }

    const setupListHighlightHoverState = (listEl, itemSelector, highlightingClass, activeClass) => {
      if (this.menuActiveStyle !== "highlight") return

      const getItems = () => Array.from(listEl.querySelectorAll(itemSelector))

      const clearActive = () => {
        listEl.classList.remove(highlightingClass)
        getItems().forEach((el) => el.classList.remove(activeClass))
      }

      const setActive = (el) => {
        if (!el) return
        listEl.classList.add(highlightingClass)
        getItems().forEach((item) => item.classList.toggle(activeClass, item === el))
      }

      getItems().forEach((item) => {
        item.addEventListener("mouseenter", () => setActive(item))
      })

      listEl.addEventListener("mouseleave", clearActive)

      listEl.addEventListener("focusin", (e) => {
        const item = e.target.closest(itemSelector)
        if (item && listEl.contains(item)) setActive(item)
      })

      listEl.addEventListener("focusout", () => {
        setTimeout(() => {
          if (!listEl.contains(document.activeElement)) clearActive()
        }, 0)
      })
    }

    // Highlight mode: dim siblings at top level and in horizontal/vertical dropdowns (not mega).
    const setupHighlightHoverState = () => {
      if (this.menuActiveStyle !== "highlight") return
      const headerMenu = this.querySelector(".header__menu")
      if (headerMenu) {
        setupListHighlightHoverState(
          headerMenu,
          ":scope > a.main-nav-item, :scope > toggletip-engine > .main-nav-item",
          "header__menu--highlighting",
          "main-nav-item--active",
        )
      }

      getDropdownMenus().forEach((menu) => {
        setupListHighlightHoverState(menu, ":scope > li", "nav__menu--highlighting", "nav__menu-item--active")
      })

      getDropdownSubmenus().forEach((submenu) => {
        setupListHighlightHoverState(
          submenu,
          ":scope > li",
          "nav__submenu--highlighting",
          "nav__submenu-item--active",
        )
      })
    }

    // Helper functions for toggletip handling
    const handleParentToggletip = (toggletip, action) => {
      if (action === "open") {
        closeAllToggletips(false, toggletip)
        toggletip.open()
        setActiveMenuItem(toggletip, true)
      } else if (action === "close") {
        const activeChild = toggletip.querySelector(".toggletip-child [data-tooltip-trigger][open='true']")
        if (!activeChild) {
          toggletip.close()
          setActiveMenuItem(toggletip, false)
        }
      }
    }

    const handleChildToggletip = (toggletip, parentToggletip, action) => {
      if (action === "open") {
        if (parentToggletip) {
          const siblings = parentToggletip.querySelectorAll(".toggletip-child")
          siblings.forEach((sibling) => {
            if (sibling !== toggletip && sibling.isOpen) {
              sibling.close()
            }
          })
        }
        toggletip.open()
        setActiveMenuItem(toggletip, true)
      } else if (action === "close") {
        toggletip.close()
        setActiveMenuItem(toggletip, false)
      }
    }

    const getParentToggletip = (element) => {
      const parent = element.closest(".toggletip-parent")
      return parent || null
    }

    const isModifiedClick = (event) => {
      if (!event) return false
      return (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        (typeof event.button === "number" && event.button !== 0)
      )
    }

    const setupToggletipEvents = (navigationMenuItem, eventType) => {
      if (navigationMenuItem.classList.contains('toggletip-parent')) {
        if (eventType === 'click') {
          navigationMenuItem.addEventListener("click", (e) => {
            const trigger = e.target.closest('[data-tooltip-trigger]')
            if (!trigger) return
            if (!isModifiedClick(e)) e.preventDefault()
            e.stopPropagation()
            const clickedToggletip = e.target.closest('toggletip-engine')
            if (!clickedToggletip) return

            const action = navigationMenuItem.isOpen ? 'close' : 'open'
            handleParentToggletip(navigationMenuItem, action)
          })
        } else {
          applyHoverWithGracePeriod(navigationMenuItem);
        }
      } else if (navigationMenuItem.classList.contains('toggletip-child')) {
        const handleChildToggle = (e) => {
          const trigger = e.target.closest('[data-tooltip-trigger]')
          if (!trigger) return
          if (!isModifiedClick(e)) e.preventDefault()
          e.stopPropagation()
          const clickedToggletip = e.target.closest('toggletip-engine')
          if (!clickedToggletip) return

          const parentToggletip = getParentToggletip(clickedToggletip)
          const action = clickedToggletip.isOpen ? 'close' : 'open'
          handleChildToggletip(clickedToggletip, parentToggletip, action)
        };

        // Find the actual focusable element for child toggletips
        const childFocusableElement = navigationMenuItem.querySelector('[data-tooltip-trigger]') || navigationMenuItem;

        if (eventType === 'click') {
          navigationMenuItem.addEventListener("click", handleChildToggle)

          // Add keyboard support for child toggletips (Space opens menu, Enter navigates)
          childFocusableElement.addEventListener("keydown", (e) => {
            if (e.key === ' ') {
              e.preventDefault()
              handleChildToggle(e)
            } else if (e.key === 'Enter') {
              // Let the default behavior happen (navigate to the link)
            }
          })
        } else {
          let closeTimer
          const dropdown = navigationMenuItem.querySelector('[data-tooltip]')

          const openChild = () => {
            if (closeTimer) clearTimeout(closeTimer)
            const parentToggletip = getParentToggletip(navigationMenuItem)
            handleChildToggletip(navigationMenuItem, parentToggletip, 'open')
          }

          const startCloseTimer = () => {
            closeTimer = setTimeout(() => {
              const parentToggletip = getParentToggletip(navigationMenuItem)
              handleChildToggletip(navigationMenuItem, parentToggletip, 'close')
            }, 150)
          }

          navigationMenuItem.addEventListener("mouseenter", () => {
            openChild()
          });
          navigationMenuItem.addEventListener("mouseleave", () => {
            startCloseTimer()
          })

          if (dropdown) {
            dropdown.addEventListener("mouseenter", () => {
              if (closeTimer) clearTimeout(closeTimer)
            })
            dropdown.addEventListener("mouseleave", () => {
              startCloseTimer()
            })
          }

          // Add keyboard support for child toggletips
          childFocusableElement.addEventListener("focus", () => {
            const parentToggletip = getParentToggletip(navigationMenuItem)
            handleChildToggletip(navigationMenuItem, parentToggletip, 'open')
          });
          childFocusableElement.addEventListener("blur", () => {
            // Use a delay to allow focus to move to child dropdown content
            setTimeout(() => {
              const activeElement = document.activeElement;
              const isWithinChildToggletip = navigationMenuItem.contains(activeElement) ||
                                           (navigationMenuItem.querySelector('[data-tooltip]') &&
                                            navigationMenuItem.querySelector('[data-tooltip]').contains(activeElement));

              if (!isWithinChildToggletip) {
                handleChildToggletip(navigationMenuItem, null, 'close')
              }
            }, 50);
          })
        }
      } else {
        // For non-menu toggletips or simple toggletips
        const isSearchTrigger = navigationMenuItem.querySelector('.search-trigger');
        const isMenuTrigger = navigationMenuItem.querySelector('.menu-trigger');
        if (isSearchTrigger || isMenuTrigger) {
          const handleToggle = (e) => {
            e.stopPropagation();
            if (navigationMenuItem.isOpen) {
              navigationMenuItem.close();
            } else {
              closeAllToggletips(true, navigationMenuItem);
              navigationMenuItem.open();
            }
          };

          // Handle mouse clicks
          navigationMenuItem.addEventListener("click", handleToggle);

          // Handle keyboard activation (Space and Enter)
          navigationMenuItem.addEventListener("keydown", (e) => {
            if (e.key === ' ') {
              e.preventDefault();
              handleToggle(e);
            }
          });
        } else if (eventType === 'hover') {
          applyHoverWithGracePeriod(navigationMenuItem);
        }
      }
    }

    // Set up event listeners based on dropdown open mode (hover vs click)
    const eventType = this.dropdownOpen === "click" ? "click" : "hover"
    this.navigationItems.forEach((navigationMenuItem) => {
      setupToggletipEvents(navigationMenuItem, eventType)
    })

    setupHighlightHoverState()

    // Add global focus handler to close menus when focus moves outside navigation
    document.addEventListener('focusin', (e) => {
      const focusedElement = e.target

      // Get all open toggletips (open attribute is on the trigger, not the toggletip-engine)
      const openToggletips = this.querySelectorAll('toggletip-engine [data-tooltip-trigger][open="true"]')
      // Check if the focused element is within any open toggletip (including dropdown content)
      let isWithinOpenToggletip = false

      for (const trigger of openToggletips) {
        // Get the parent toggletip-engine element
        const toggletip = trigger.closest('toggletip-engine')
        if (!toggletip) continue

        // Check if focused element is within the toggletip container
        if (toggletip.contains(focusedElement)) {
          isWithinOpenToggletip = true
          break
        }

        // Also check if it's within the dropdown content area
        const dropdown = toggletip.querySelector('[data-tooltip]')
        if (dropdown && dropdown.contains(focusedElement)) {
          isWithinOpenToggletip = true
          break
        }
      }

      // If focus is within navigation but not within any open toggletip, close menus
      if (this.contains(focusedElement) && !isWithinOpenToggletip) {
        closeAllToggletips()
        setActiveMenuItem(null, false)
      }
    })

    // Add section click handler for click mode (close when clicking outside menus)
    if (this.dropdownOpen === "click") {
      this.closest(".shopify-section").addEventListener("click", () => {
        closeAllToggletips()
        setActiveMenuItem(null, false)
      })
    }

    const headerNavEls = this.headerEl ? Array.from(this.headerEl.querySelectorAll(".nav")) : Array.from(this.querySelectorAll(".nav"))
    const drawerNavEls = Array.from(document.querySelectorAll('drawer-component[data-drawer="nav-menu"] .nav'))
    const navEls = [...new Set([...headerNavEls, ...drawerNavEls])]

    navEls.forEach((nav) => {
      resetSecondaryMenu(nav)
    })

    navEls.forEach((el) => {
      el.props = {}

      const isDrawer = el.closest(".nav-drawer") != null

      if (isDrawer) {
        const drawerMenuStyle = mediaQueries.isLargeDown.matches
          ? (el.dataset.drawerMenuStyle || "accordion")
          : (el.dataset.desktopDrawerMenuStyle || el.dataset.drawerMenuStyle || "accordion")
        const drawerAccordion = drawerMenuStyle === "accordion"

        if (drawerAccordion) {
          mobileAccordions(el)
          return
        }

        // Paged drawer: L2/L3 pages via switchContent / switchTertiaryContent
        const innerContainer = el.querySelector(".nav--mobile__inner-container")

        const scrollToTop = () => {
          if (innerContainer) {
            requestAnimationFrame(() => {
              innerContainer.scrollTop = 0
              innerContainer.scrollLeft = 0
              if (typeof innerContainer.scrollTo === "function") {
                innerContainer.scrollTo({ top: 0, left: 0, behavior: "auto" })
              }
            })
          }
        }

        el.querySelectorAll(".nav__main-menu-link").forEach((button) => {
          button.addEventListener("click", () => {
            const targetId = button.getAttribute("data-targetmenu") || ""
            if (!targetId) return
            if (targetId.startsWith("navigation-item-l3-")) {
              switchTertiaryContent(button, el)
              scrollToTop()
            } else {
              el.classList.remove("nav--tertiary-menu-open")
              resetTertiaryMenu(el)
              el.classList.add("nav--secondary-menu-open")
              switchContent(button, el)
              scrollToTop()
            }
          })
        })

        el.querySelectorAll(".nav__secondary-menu__close").forEach((button) => {
          button.addEventListener("click", () => {
            resetSecondaryMenu(el)
            resetTertiaryMenu(el)
            scrollToTop()
          })
        })

        el.querySelectorAll(".nav__tertiary-menu__close").forEach((button) => {
          button.addEventListener("click", () => {
            resetTertiaryMenu(el)
            scrollToTop()
          })
        })
        return
      }

      // Desktop nav: legacy multi-level mega menu logic removed in favor of new mega menu implementation.
    })
  }

  observeStickySubheader() {
    const stickySubheader = document.querySelector(".sticky-subheader")
    if (!stickySubheader) return

    stickySubheader.props = { isStuck: false }

    const observer = new IntersectionObserver(
      ([e]) => {
        const isStuck = e.intersectionRatio < 1
        e.target.classList.toggle("sticky--stuck", isStuck)

        const shouldUpdate = e.target.props.isStuck !== isStuck

        if (shouldUpdate) e.target.props.isStuck = isStuck
      },
      { threshold: [1] },
    )

    observer.observe(stickySubheader)
  }

  // reset stuff when resizing from desktop to mobile
  resetOnResize() {
    mediaQueries.isLargeDown.addEventListener("change", () => {
      closeAllToggletips()
      this.querySelectorAll(".main-nav-item--open").forEach((el) => {
        el.classList.remove("main-nav-item--open")
      })
      const sectionRoot = this.closest(".shopify-section") || this
      sectionRoot
        .querySelectorAll(".nav__menu-item--open, .nav__menu-item--active, .nav__submenu-item--active")
        .forEach((el) => {
          el.classList.remove("nav__menu-item--open", "nav__menu-item--active", "nav__submenu-item--active")
        })
      sectionRoot.querySelectorAll(".nav__menu--highlighting, .nav__submenu--highlighting").forEach((el) => {
        el.classList.remove("nav__menu--highlighting", "nav__submenu--highlighting")
      })

      if (mediaQueries.isLargeDown.matches) {
        this.headerEl.style.marginTop = 0
        this.headerEl.props.navMargin = 0
        this.headerEl.classList.remove("nav-wrapper--solid")
      }

      this.#updateNavDrawerLayout()
    })
  }

  #getVisibleBottom(element) {
    if (!element) return 0
    const computedStyle = window.getComputedStyle(element)
    if (computedStyle.display === "none" || computedStyle.visibility === "hidden") return 0
    const rect = element.getBoundingClientRect()
    if (rect.bottom <= 0 || rect.top >= window.innerHeight) return 0
    return rect.bottom
  }

  #updateNavDrawerLayout = () => {
    const navDrawerEl = this.#resolveNavDrawerEl()
    if (!navDrawerEl) return

    if (!mediaQueries.isLargeDown.matches) {
      navDrawerEl.style.removeProperty("--nav-drawer-mobile-top")
      navDrawerEl.style.removeProperty("--nav-drawer-mobile-left")
      navDrawerEl.style.removeProperty("--nav-drawer-mobile-right")
      navDrawerEl.style.removeProperty("--nav-drawer-mobile-border-radius")
      return
    }

    const announcementSection = document.querySelector(".shopify-section.announcement-bar")
    const announcementWrapper = announcementSection?.querySelector(".section__wrapper")
    const isFloatingOnMobile = this.dataset.floatingStyle === "all_devices"
    const isEdgeToEdge = document.documentElement.dataset.drawerPosition === "edge_to_edge"
    const navAnchor = isFloatingOnMobile && this.sectionWrapper ? this.sectionWrapper : this.headerEl
    const announcementBottom = this.#getVisibleBottom(announcementWrapper)
    const navBottom = this.#getVisibleBottom(navAnchor)
    const topOffset = Math.max(0, Math.round(Math.max(announcementBottom, navBottom)))
    const sectionWrapperRect = this.sectionWrapper?.getBoundingClientRect()
    const leftOffset = isEdgeToEdge
      ? 0
      : isFloatingOnMobile && sectionWrapperRect
        ? Math.max(0, Math.round(sectionWrapperRect.left))
        : 0
    const rightOffset = isEdgeToEdge
      ? 0
      : isFloatingOnMobile && sectionWrapperRect
        ? Math.max(0, Math.round(window.innerWidth - sectionWrapperRect.right))
        : 0
    const borderRadius = isEdgeToEdge
      ? "0"
      : isFloatingOnMobile && this.sectionWrapper
        ? window.getComputedStyle(this.sectionWrapper).borderRadius
        : "0"

    navDrawerEl.style.setProperty("--nav-drawer-mobile-top", `${topOffset}px`)
    navDrawerEl.style.setProperty("--nav-drawer-mobile-left", `${leftOffset}px`)
    navDrawerEl.style.setProperty("--nav-drawer-mobile-right", `${rightOffset}px`)
    navDrawerEl.style.setProperty("--nav-drawer-mobile-border-radius", borderRadius)
  }

  handleCartDrawer() {
    if (this.cartOpenButtons) {
      this.cartOpenButtons.forEach((button) => {
        button.addEventListener("click", () => {
          closeAllToggletips(true)

          this.cartDrawer.open()
        })
      })
    }
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.handleScroll)
    if (this._handleNavDrawerOpen) {
      document.removeEventListener("drawer:open", this._handleNavDrawerOpen)
    }
    if (this._handleNavDrawerClose) {
      document.removeEventListener("drawer:close", this._handleNavDrawerClose)
    }
    document.querySelectorAll(".shopify-section.shopify-section-group-main-header").forEach((el) => {
      el.classList.remove("above-overlay")
    })
    document.body.classList.remove("nav-overlay-active")
    document.body.classList.remove(...NavigationMain.mirroredBodyHeaderClasses)

    if (this.sectionWrapper && this.defaultScheme && this.overlayScheme) {
      this.sectionWrapper.classList.remove("color-" + this.overlayScheme)
      this.sectionWrapper.classList.add("color-" + this.defaultScheme)
    }

    this.headerEl?.classList.remove("header--overlay-scheme-active")

    if (this.debouncedUpdateNavDrawerLayout) {
      window.removeEventListener("resize", this.debouncedUpdateNavDrawerLayout)
      window.removeEventListener("orientationchange", this.debouncedUpdateNavDrawerLayout)
    }
  }

  // Simple debounce utility scoped to the instance
  _debounce(fn, wait) {
    let timeoutId
    return (...args) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => fn.apply(this, args), wait)
    }
  }
}

if (!window.customElements.get("main-nav")) {
  window.customElements.define("main-nav", NavigationMain)
}
